/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Organizations.tsx
 * DESCRIPTION: Organization management page for multi-tenant support.
 *              Allows users to view organization details, manage members,
 *              configure settings, and view activity logs.
 *              Updated with dark mode support and improved UI/UX.
 *
 * FEATURES:
 * - Organization stats in dashboard-style cards
 * - Tabbed interface for Members, Settings, and Activity
 * - Member management with roles and permissions
 * - Dark mode support throughout
 * - Fully functional with mock data
 * - Ready for live API integration
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2025-01-15
 * @updated 2026-03-27 - Redesigned with dark mode, tabbed interface,
 *                       dashboard-style cards, improved UX
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useOrganization, useClerk } from '@clerk/react';
import {
  Users,
  Settings,
  Activity,
  Plus,
  MoreVertical,
  Mail,
  Shield,
  Trash2,
  Edit,
  Check,
  X,
  Clock,
  Building2,
  CreditCard,
  TrendingUp,
  FileText,
} from 'lucide-react';

/**
 * Organization Member Type
 */
interface OrgMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  avatar?: string;
  joinedDate: string;
  lastActive: string;
  status: 'active' | 'pending' | 'suspended';
}

/**
 * Organization Activity Type
 */
interface OrgActivity {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  details: string;
}

/**
 * Organization Settings Type
 */
interface OrgSettings {
  name: string;
  slug: string;
  domain?: string;
  maxMembers: number;
  features: {
    aiAnalysis: boolean;
    apiAccess: boolean;
    advancedReporting: boolean;
    prioritySupport: boolean;
  };
}

/**
 * Organizations Component
 * Manages organization members, settings, and activity logs.
 *
 * @returns {JSX.Element} Organization management interface
 */
const Organizations: React.FC = () => {
  const { user } = useUser();
  const { organization, membership, isLoaded: orgLoaded } = useOrganization();
  const { signOut } = useClerk();
  
  const [activeTab, setActiveTab] = useState<'members' | 'settings' | 'activity'>('members');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [activities, setActivities] = useState<OrgActivity[]>([]);
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgMember['role']>('member');

  /**
   * Load organization data on mount
   */
  useEffect(() => {
    if (orgLoaded && organization) {
      loadOrganizationData();
    }
  }, [orgLoaded, organization]);

  const loadOrganizationData = async () => {
    try {
      // Mock API calls - replace with real endpoints
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock members data
      setMembers([
        {
          id: '1',
          name: user?.firstName || 'You',
          email: user?.emailAddresses[0]?.emailAddress || '',
          role: membership?.role as OrgMember['role'] || 'owner',
          avatar: user?.imageUrl,
          joinedDate: '2026-01-15',
          lastActive: 'Just now',
          status: 'active',
        },
        {
          id: '2',
          name: 'Sarah Chen',
          email: 'sarah@company.com',
          role: 'admin',
          joinedDate: '2026-02-20',
          lastActive: '2 hours ago',
          status: 'active',
        },
        {
          id: '3',
          name: 'Mike Johnson',
          email: 'mike@company.com',
          role: 'member',
          joinedDate: '2026-03-10',
          lastActive: '1 day ago',
          status: 'active',
        },
        {
          id: '4',
          name: 'Emma Wilson',
          email: 'emma@company.com',
          role: 'member',
          joinedDate: '2026-03-25',
          lastActive: '3 days ago',
          status: 'pending',
        },
      ]);

      // Mock activity data
      setActivities([
        {
          id: '1',
          action: 'Member invited',
          user: 'Sarah Chen',
          timestamp: '2026-03-25 14:30',
          details: 'Invited emma@company.com as member',
        },
        {
          id: '2',
          action: 'Settings updated',
          user: 'You',
          timestamp: '2026-03-24 10:15',
          details: 'Updated organization name and domain',
        },
        {
          id: '3',
          action: 'Role changed',
          user: 'Sarah Chen',
          timestamp: '2026-03-20 16:45',
          details: 'Changed mike@company.com to member',
        },
        {
          id: '4',
          action: 'Member joined',
          user: 'Mike Johnson',
          timestamp: '2026-03-10 09:00',
          details: 'Accepted invitation and joined organization',
        },
      ]);

      // Mock settings data
      setSettings({
        name: organization?.name || 'My Organization',
        slug: organization?.slug || 'my-org',
        domain: 'company.com',
        maxMembers: 50,
        features: {
          aiAnalysis: true,
          apiAccess: true,
          advancedReporting: false,
          prioritySupport: false,
        },
      });
    } catch (error) {
      console.error('Failed to load organization data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle member invitation
   */
  const handleInviteMember = async () => {
    if (!inviteEmail) return;
    
    setIsProcessing(true);
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMember: OrgMember = {
        id: Date.now().toString(),
        name: inviteEmail.split('@')[0],
        email: inviteEmail,
        role: inviteRole,
        joinedDate: new Date().toISOString().split('T')[0],
        lastActive: 'Never',
        status: 'pending',
      };
      
      setMembers(prev => [...prev, newMember]);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      
      // Add to activity
      setActivities(prev => [{
        id: Date.now().toString(),
        action: 'Member invited',
        user: user?.firstName || 'You',
        timestamp: new Date().toLocaleString(),
        details: `Invited ${inviteEmail} as ${inviteRole}`,
      }, ...prev]);
    } catch (error) {
      console.error('Failed to invite member:', error);
      alert('Failed to send invitation');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle member role change
   */
  const handleRoleChange = async (memberId: string, newRole: OrgMember['role']) => {
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));
      
      // Add to activity
      const member = members.find(m => m.id === memberId);
      if (member) {
        setActivities(prev => [{
          id: Date.now().toString(),
          action: 'Role changed',
          user: user?.firstName || 'You',
          timestamp: new Date().toLocaleString(),
          details: `Changed ${member.email} to ${newRole}`,
        }, ...prev]);
      }
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update role');
    }
  };

  /**
   * Handle member removal
   */
  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setMembers(prev => prev.filter(m => m.id !== memberId));
      
      // Add to activity
      const member = members.find(m => m.id === memberId);
      if (member) {
        setActivities(prev => [{
          id: Date.now().toString(),
          action: 'Member removed',
          user: user?.firstName || 'You',
          timestamp: new Date().toLocaleString(),
          details: `Removed ${member.email} from organization`,
        }, ...prev]);
      }
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('Failed to remove member');
    }
  };

  /**
   * Handle settings update
   */
  const handleUpdateSettings = async () => {
    if (!settings) return;
    
    setIsProcessing(true);
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Add to activity
      setActivities(prev => [{
        id: Date.now().toString(),
        action: 'Settings updated',
        user: user?.firstName || 'You',
        timestamp: new Date().toLocaleString(),
        details: 'Updated organization settings',
      }, ...prev]);
      
      alert('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      alert('Failed to update settings');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Get role badge color
   */
  const getRoleBadge = (role: OrgMember['role']) => {
    const badges = {
      owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      member: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    };
    return badges[role];
  };

  /**
   * Get status badge color
   */
  const getStatusBadge = (status: OrgMember['status']) => {
    const badges = {
      active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return badges[status];
  };

  if (isLoading) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Organizations
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your organization, members, and settings
          </p>
        </div>

        {/* Organization Stats - Dashboard Style Cards */}
        {organization && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {organization.name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      @{organization.slug} • {membership?.role}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('settings')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  Edit Settings
                </button>
              </div>

              {/* Usage Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Members</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {members.length} <span className="text-sm font-normal text-gray-500">/ {settings?.maxMembers}</span>
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(members.length / (settings?.maxMembers || 50)) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Files Analyzed</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        1,247 <span className="text-sm font-normal text-gray-500">this month</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                    <TrendingUp className="w-4 h-4" />
                    <span>+12% vs last month</span>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <CreditCard className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Plan</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        Pro
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Renews in 23 days
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <Activity className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Recent Activity</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {activities.length}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Last: {activities[0]?.timestamp}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabbed Content: Members, Settings, Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="flex gap-6">
              <button
                onClick={() => setActiveTab('members')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'members'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Users className="w-4 h-4" />
                Members ({members.length})
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'activity'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Activity className="w-4 h-4" />
                Activity
              </button>
            </nav>
          </div>

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Organization Members
                </h2>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Invite Member
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Member</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Role</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Joined</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Last Active</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {members.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {member.avatar ? (
                              <img
                                src={member.avatar}
                                alt={member.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-medium">
                                  {member.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value as OrgMember['role'])}
                            disabled={membership?.role !== 'owner'}
                            className={`px-3 py-1 rounded-full text-xs font-medium border-none cursor-pointer ${getRoleBadge(member.role)} ${
                              membership?.role !== 'owner' ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                          </select>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(member.status)}`}>
                            {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(member.joinedDate).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {member.lastActive}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {membership?.role === 'owner' && member.role !== 'owner' && (
                              <>
                                <button
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                  title="Remove member"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  title="Edit member"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && settings && (
            <div className="max-w-2xl">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Organization Settings
              </h2>
              
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      value={settings.name}
                      onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organization Slug
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 dark:text-gray-400">verimark.app/</span>
                      <input
                        type="text"
                        value={settings.slug}
                        onChange={(e) => setSettings({ ...settings, slug: e.target.value })}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Custom Domain (optional)
                    </label>
                    <input
                      type="text"
                      value={settings.domain || ''}
                      onChange={(e) => setSettings({ ...settings, domain: e.target.value })}
                      placeholder="yourcompany.com"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Member Limits */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Member Limits</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum Members
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={settings.maxMembers}
                      onChange={(e) => setSettings({ ...settings, maxMembers: Number(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Current: {members.length} members
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Enabled Features</h3>
                  
                  <div className="space-y-3">
                    {[
                      { key: 'aiAnalysis', label: 'AI Analysis', desc: 'Enable AI-powered file analysis' },
                      { key: 'apiAccess', label: 'API Access', desc: 'Allow API access for integrations' },
                      { key: 'advancedReporting', label: 'Advanced Reporting', desc: 'Access detailed analytics and reports' },
                      { key: 'prioritySupport', label: 'Priority Support', desc: 'Get faster response times from support' },
                    ].map((feature) => (
                      <div key={feature.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{feature.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
                        </div>
                        <button
                          onClick={() => setSettings({
                            ...settings,
                            features: {
                              ...settings.features,
                              [feature.key]: !settings.features[feature.key as keyof typeof settings.features],
                            },
                          })}
                          className={`w-11 h-6 rounded-full transition-colors ${
                            settings.features[feature.key as keyof typeof settings.features]
                              ? 'bg-blue-600'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                            settings.features[feature.key as keyof typeof settings.features]
                              ? 'translate-x-5'
                              : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleUpdateSettings}
                    disabled={isProcessing}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Organization Activity
              </h2>
              
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 dark:text-white">{activity.action}</p>
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {activity.timestamp}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        by <span className="font-medium text-gray-900 dark:text-white">{activity.user}</span>
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{activity.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invite Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="member@company.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['owner', 'admin', 'member'] as const).map((role) => (
                    <button
                      key={role}
                      onClick={() => setInviteRole(role)}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        inviteRole === role
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-center mb-2">
                        <Shield className={`w-5 h-5 ${
                          role === 'owner' ? 'text-purple-600' :
                          role === 'admin' ? 'text-blue-600' :
                          'text-gray-600'
                        }`} />
                      </div>
                      <p className={`text-sm font-medium capitalize ${
                        inviteRole === role
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {role}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteMember}
                disabled={isProcessing || !inviteEmail}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
